import { Formik } from 'formik'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import api from '../../../config/AxiosInstance'
import * as Common from '../../../utils/Common'
import { updateCOA } from '../../../utils/validationSchema'
import CustomButton from '../../Button/CustomButton'
import DatePickerComponent from '../../Input/DatePicker'
import { Plus, Trash2 } from 'lucide-react'

const labelDetailStyle = {
  fontSize: '13px',
  fontWeight: '700',
  color: '#6c757d',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const StyledTextField = ({
  label,
  value,
  name,
  handleChange,
  handleBlur,
  touched,
  errors,
  type = 'text',
  placeholder,
  required,
  disabled,
  as,
  col = 12
}) => {
  return (
    <Form.Group className={`mb-3 mt-3 col-12 col-md-${col} col-lg-${col}`}>
      <Form.Label style={labelDetailStyle}>
        {label} {required && <sup className="text-danger">*</sup>}
      </Form.Label>
      <Form.Control
        as={as}
        type={type}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        isInvalid={touched?.[name] && !!errors?.[name]}
        placeholder={placeholder}
        disabled={disabled}
        className={`px-3 ${disabled ? 'bg-light' : 'bg-white'}`}
        style={{
          fontSize: '0.9rem',
          height: as === 'textarea' ? 'auto' : '45px',
          borderRadius: '5px',
          border: '1px solid #e0e0e0'
        }}
        autoComplete="off"
      />
      <Form.Control.Feedback type="invalid">
        {errors?.[name]}
      </Form.Control.Feedback>
    </Form.Group>
  )
}

const StyledSelectInput = ({
  label,
  value,
  name,
  handleChange,
  handleBlur,
  touched,
  errors,
  options,
  required,
  disabled,
  col = 12,
  placeholder
}) => {
  return (
    <Form.Group className={`mb-3 mt-3 col-12 col-md-${col} col-lg-${col}`}>
      <Form.Label style={labelDetailStyle}>
        {label} {required && <sup className="text-danger">*</sup>}
      </Form.Label>
      <Form.Select
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        isInvalid={touched?.[name] && !!errors?.[name]}
        disabled={disabled}
        className={`px-3 ${disabled ? 'bg-light' : 'bg-white'}`}
        style={{
          fontSize: '0.9rem',
          height: '45px',
          borderRadius: '5px',
          border: '1px solid #e0e0e0'
        }}
      >
        <option value="">{placeholder || 'Select'}</option>
        {options.map((option, idx) => (
          <option key={idx} value={option.uom_name || option.value}>
            {option.uom_name || option.name || option.label}
          </option>
        ))}
      </Form.Select>
      <Form.Control.Feedback type="invalid">
        {errors?.[name]}
      </Form.Control.Feedback>
    </Form.Group>
  )
}

function ModalUpdateItemCoaPrDraftDetail(props) {
  const { selecteditemcoa, pr_id } = props
  const [uomOptions, setUomOptions] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)

  const fetchUomOptions = useCallback(async () => {
    if (selecteditemcoa?.item_id) {
      try {
        const res = await api.get(
          `/master/items/${selecteditemcoa.item_id}/uoms`
        )
        setUomOptions(res.data.data)
      } catch (err) {
        console.error('Failed to fetch UOM options', err)
      }
    }
  }, [selecteditemcoa?.item_id])

  const fetchBatchData = useCallback(async () => {
    if (selecteditemcoa?.batch_id) {
      try {
        const res = await api.get(`/master/batch/${selecteditemcoa.batch_id}`)
        if (res.data.success && res.data.data) {
          const batchData = res.data.data
          if (batchData.coa) {
            setPreviewFile({
              name: batchData.coa,
              contentId: selecteditemcoa.coa_document_id || batchData.id
            })
          } else {
            setPreviewFile(null)
          }
        }
      } catch (err) {
        console.error('Failed to fetch batch data', err)
      }
    }
  }, [selecteditemcoa?.batch_id, selecteditemcoa?.coa_document_id])

  useEffect(() => {
    if (props.show) {
      fetchUomOptions()
      if (selecteditemcoa?.batch_id) {
        fetchBatchData()
      } else if (selecteditemcoa?.coa_document_id) {
        setPreviewFile({
          name: 'COA Document',
          contentId: selecteditemcoa.coa_document_id
        })
      } else {
        setPreviewFile(null)
      }
    }
  }, [props.show, fetchUomOptions, fetchBatchData, selecteditemcoa])

  const handleFileUpload = async (file) => {
    const batchNumber = selecteditemcoa?.batch
    if (!batchNumber) {
      Common.showPopUpErrorMessage('Batch number not found')
      return
    }

    try {
      setIsUploading(true)
      const formData = new FormData()
      formData.append('batch', batchNumber.toUpperCase())
      formData.append('file', file)

      const response = await api.post(
        '/inbound/pr-draft/upload-coa',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )

      if (response.data.success) {
        const uploadedData = response.data.data

        setPreviewFile({
          name: uploadedData?.name || file.name,
          contentId: uploadedData?.contentId || ''
        })
        Common.showPopUpSuccessMessage('COA uploaded successfully')
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message || 'Failed to upload COA'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (values) => {
    try {
      const payload = {
        total_pack: values.total_pack,
        date_md: values.coa_pd,
        date_ed: values.coa_ed,
        note: values.note,
        receiving_uom: values.receiving_uom,
        coa_document_id: previewFile?.contentId || null
      }

      const res = await api.put(
        `/inbound/pr-draft/${pr_id}/coa/${selecteditemcoa.id}`,
        payload
      )

      if (res.data.success) {
        Common.showPopUpSuccessMessage(res.data.message)
        props.onHide()
        props.refreshdata()
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  return (
    <Modal {...props} centered size="lg">
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold text-muted">Update</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <Formik
          initialValues={{
            id: selecteditemcoa?.id,
            coa_pd: selecteditemcoa?.coa_md || selecteditemcoa?.coa_pd || '',
            coa_ed: selecteditemcoa?.coa_ed || '',
            total_pack: selecteditemcoa?.total_pack || '',
            receiving_uom:
              selecteditemcoa?.uom_pack || selecteditemcoa?.receiving_uom || '',
            note: selecteditemcoa?.note || ''
          }}
          enableReinitialize={true}
          validationSchema={updateCOA}
          onSubmit={(values) => {
            const formattedValues = {
              ...values,
              coa_pd: values.coa_pd
                ? moment(values.coa_pd).format('YYYY-MM-DD')
                : null,
              coa_ed: values.coa_ed
                ? moment(values.coa_ed).format('YYYY-MM-DD')
                : null
            }
            handleSubmit(formattedValues)
          }}
        >
          {({
            handleBlur,
            handleSubmit,
            handleChange,
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched
          }) => {
            const currentUom = uomOptions.find(
              (u) => u.uom_name === values.receiving_uom
            )
            const baseUomQty =
              values.total_pack * (currentUom?.convertion_factor || 0)

            return (
              <Form onSubmit={handleSubmit}>
                <Row className="mb-4">
                  <Col md={12}>
                    <h4 className="fw-bold mb-0">{selecteditemcoa?.item}</h4>
                  </Col>
                </Row>

                <Row>
                  <StyledTextField
                    label="batch"
                    name="batch"
                    value={selecteditemcoa?.batch || ''}
                    disabled
                    col={12}
                  />
                  <DatePickerComponent
                    label="manufacturing date"
                    name="coa_pd"
                    value={values.coa_pd}
                    handleChange={setFieldValue}
                    handleBlur={() => setFieldTouched('coa_pd', true)}
                    errors={errors}
                    touched={touched}
                    col={6}
                    required
                  />
                  <DatePickerComponent
                    label="exp. date"
                    name="coa_ed"
                    value={values.coa_ed}
                    handleChange={setFieldValue}
                    handleBlur={() => setFieldTouched('coa_ed', true)}
                    errors={errors}
                    touched={touched}
                    col={6}
                    required
                  />
                  <StyledTextField
                    label="note"
                    name="note"
                    value={values.note}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    placeholder="Input"
                    col={12}
                  />

                  <Col md={12} className="mb-4 mt-3">
                    <Form.Label style={labelDetailStyle}>COA</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      {!previewFile ? (
                        <div className="position-relative">
                          <input
                            type="file"
                            className="position-absolute opacity-0 w-100 h-100"
                            style={{ cursor: 'pointer', zIndex: 1 }}
                            onChange={(e) =>
                              handleFileUpload(e.target.files[0])
                            }
                            accept="application/pdf"
                          />
                          <Button
                            variant="white"
                            className="border rounded-3 d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <Plus size={16} className="text-muted" />
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2 border rounded-3 p-2 bg-light">
                          <span
                            className="text-truncate"
                            style={{ maxWidth: '200px' }}
                          >
                            {previewFile.name}
                          </span>
                          <Button
                            variant="link"
                            className="text-danger p-0"
                            onClick={() => setPreviewFile(null)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      )}
                      {isUploading && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </div>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h5 className="fw-bold mb-4">Receiving</h5>
                <Row>
                  <StyledTextField
                    label="receiving"
                    name="total_pack"
                    type="number"
                    value={values.total_pack}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    col={4}
                    required
                    placeholder="Input"
                  />
                  <StyledSelectInput
                    label="receiving uom"
                    name="receiving_uom"
                    value={values.receiving_uom}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    options={uomOptions}
                    col={4}
                    required
                  />
                  <StyledTextField
                    label="base uom qty"
                    name="base_uom_qty"
                    value={baseUomQty}
                    disabled
                    col={4}
                  />
                </Row>

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button
                    variant="link"
                    className="text-muted text-decoration-none"
                    onClick={props.onHide}
                  >
                    Cancel
                  </Button>
                  <CustomButton
                    onClick={handleSubmit}
                    variant="warning"
                    text="Save"
                    style={{ width: '120px' }}
                  />
                </div>
              </Form>
            )
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  )
}

export default ModalUpdateItemCoaPrDraftDetail
